import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";

interface Product {
  id: string;
  sku: string | null;
  title: string;
  description: string | null;
  brand: string | null;
  category: string | null;
  price: number | null;
  currency: string;
  image_url: string | null;
  thumbnail_url: string | null;
  status: "draft" | "active" | "archived";
  embedding_status: "pending" | "processing" | "complete" | "failed";
  attributes_json: string | null;
  created_at: string;
  updated_at: string;
}

interface VisionAnalysis {
  brand?: string;
  category?: string;
  productType?: string;
  condition?: number;
  colors?: string[];
  material?: string;
  estimatedRetailPrice?: number;
  description?: string;
  source?: string;
}

interface PricingIntelligence {
  // Core price data from marketplace scraping
  minPrice?: number;
  maxPrice?: number;
  avgPrice?: number;
  confidence?: number;
  explanation?: string;

  // The search query used
  searchQuery?: string;

  // Volume and source data
  totalDataPoints?: number;
  amazonListings?: number;
  ebayListings?: number;
  sourcesUsed?: string[];
  marketplacesScraped?: string[];
  cached?: boolean;

  // Individual listings found
  listings?: Array<{
    title: string;
    price: number;
    currency?: string;
    condition?: string;
    url?: string;
    source: string;
    imageUrl?: string;
  }>;

  // Market insight for brands
  marketInsight?: {
    summary: string;
    volumeLevel: "none" | "low" | "medium" | "high" | "very_high";
    volume?: number;
    priceSpread?: number;
    avgPrice?: string;
    marketplaces?: string[];
    confidence?: string;
    recommendation: string;
  };

  // Legacy fields for backwards compatibility
  estimatedValue?: number;
  priceRange?: { min: number; max: number };
  marketTrend?: string;
  comparables?: Array<{ title: string; price: number; source: string }>;
}

interface SimilarProduct {
  id: string;
  title: string;
  brand?: string;
  price?: number;
  image_url?: string;
  score: number;
}

interface CLMEnrichment {
  taxonomyId?: string;
  attributes?: Record<string, any>;
  searchStrategy?: {
    primary_query?: string;
    fallback_queries?: string[];
    category?: string;
  };
  // Rich semantic enrichment fields
  enrichedDescription?: string;
  idealFor?: string[];
  notIdealFor?: string[];
  keyFeatures?: string[];
  userContexts?: string[];
  styleDescriptors?: string[];
  qualityTier?: string;
  pairsWellWith?: string[];
  seasonalRelevance?: string;
  careConsiderations?: string[];
  searchKeywords?: string[];
}

interface ValuationResult {
  quickSale?: { gbp: number; confidence: number };
  fairMarket?: { gbp: number; confidence: number };
  premium?: { gbp: number; confidence: number };
  marketContext?: { demand?: string; supply?: string; trend?: string };
  reasoning?: string;
}

interface EnrichmentHistory {
  id: string;
  type: "vision" | "pricing" | "embedding" | "clm" | "valuation";
  status: "success" | "failed";
  timestamp: string;
  source: string;
  duration_ms?: number;
}

export const Route = createFileRoute("/products_/$id")({
  component: ProductDetail,
});

function ProductDetail() {
  const { id } = Route.useParams() as { id: string };
  const { currentTenant } = useTenant();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [vision, setVision] = useState<VisionAnalysis | null>(null);
  const [pricing, setPricing] = useState<PricingIntelligence | null>(null);
  const [similar, setSimilar] = useState<SimilarProduct[]>([]);
  const [history, setHistory] = useState<EnrichmentHistory[]>([]);
  const [clm, setClm] = useState<CLMEnrichment | null>(null);
  const [valuation, setValuation] = useState<ValuationResult | null>(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "overview" | "vision" | "semantic" | "pricing" | "similar" | "history"
  >("overview");

  // Fetch product
  useEffect(() => {
    async function fetchProduct() {
      try {
        const response = await fetch(`/api/products/${id}`);
        if (!response.ok) throw new Error("Product not found");
        const data = await response.json();
        setProduct(data.product);

        // Parse stored attributes for any previous analysis
        if (data.product.attributes_json) {
          try {
            const attrs = JSON.parse(data.product.attributes_json);
            if (attrs.vision) setVision(attrs.vision);
            if (attrs.pricing) setPricing(attrs.pricing);
            if (attrs.clm) {
              // Map snake_case from gateway to camelCase for UI
              const clmData = attrs.clm;
              setClm({
                ...clmData,
                enrichedDescription: clmData.enriched_description || clmData.enrichedDescription,
                idealFor: clmData.ideal_for || clmData.idealFor,
                notIdealFor: clmData.not_ideal_for || clmData.notIdealFor,
                keyFeatures: clmData.key_features || clmData.keyFeatures,
                userContexts: clmData.user_contexts || clmData.userContexts,
                styleDescriptors: clmData.style_descriptors || clmData.styleDescriptors,
                qualityTier: clmData.quality_tier || clmData.qualityTier,
                pairsWellWith: clmData.pairs_well_with || clmData.pairsWellWith,
                seasonalRelevance: clmData.seasonal_relevance || clmData.seasonalRelevance,
                careConsiderations: clmData.care_considerations || clmData.careConsiderations,
                searchKeywords: clmData.search_keywords || clmData.searchKeywords,
              });
            }
            if (attrs.valuation) setValuation(attrs.valuation);
          } catch {
            // Ignore parse errors
          }
        }
      } catch (err) {
        // Fall back to demo data for Arc'teryx tenant
        if (currentTenant.id === "arcteryx") {
          const demoProduct = getArcteryxDemoProduct(id);
          if (demoProduct) {
            setProduct(demoProduct);
            setError(null);
          } else {
            setError("Product not found");
          }
        } else {
          setError(
            err instanceof Error ? err.message : "Failed to load product",
          );
        }
      } finally {
        setLoading(false);
      }
    }
    fetchProduct();
  }, [id, currentTenant.id]);

  // Run full AI analysis
  const runAnalysis = async () => {
    if (!product) return;

    setAnalyzing(true);
    const imageUrl = product.image_url || product.thumbnail_url;

    try {
      console.log("[Analysis] Starting AI analysis...");

      // Call the unified analyze endpoint
      const response = await fetch("/api/v1/products/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product: {
            id: product.id,
            title: product.title,
            description: product.description,
            imageUrl,
            brand: product.brand,
            category: product.category,
            price: product.price,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("[Analysis] Results:", data);

        // Update vision state
        if (data.vision) {
          setVision(data.vision);
          setHistory((prev) => [
            {
              id: crypto.randomUUID(),
              type: "vision",
              status: "success",
              timestamp: new Date().toISOString(),
              source: data.vision.source || "dgx-vision",
              duration_ms: data.processingTimeMs,
            },
            ...prev,
          ]);
        }

        // Update pricing state
        if (data.pricing) {
          setPricing(data.pricing);
          setHistory((prev) => [
            {
              id: crypto.randomUUID(),
              type: "pricing",
              status: "success",
              timestamp: new Date().toISOString(),
              source: "dgx-pricing",
            },
            ...prev,
          ]);
        }

        // Update similar products
        if (data.similar && data.similar.length > 0) {
          setSimilar(data.similar);
        }

        // Update CLM enrichment data
        if (data.clm) {
          setClm(data.clm);
          setHistory((prev) => [
            {
              id: crypto.randomUUID(),
              type: "clm",
              status: "success",
              timestamp: new Date().toISOString(),
              source: "dgx-gateway",
            },
            ...prev,
          ]);
        }

        // Update valuation data
        if (data.valuation) {
          setValuation(data.valuation);
          setHistory((prev) => [
            {
              id: crypto.randomUUID(),
              type: "valuation",
              status: "success",
              timestamp: new Date().toISOString(),
              source: "dgx-gateway",
            },
            ...prev,
          ]);
        }

        // Show errors if any
        if (data.errors && data.errors.length > 0) {
          console.warn("[Analysis] Some steps failed:", data.errors);
          data.errors.forEach((err: { step: string; error: string }) => {
            setHistory((prev) => [
              {
                id: crypto.randomUUID(),
                type:
                  err.step === "vision"
                    ? "vision"
                    : err.step === "embedding"
                      ? "embedding"
                      : "pricing",
                status: "failed",
                timestamp: new Date().toISOString(),
                source: err.step,
              },
              ...prev,
            ]);
          });
        }
      } else {
        // API failed - log the error and show to user
        const errorData = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        console.error("[Analysis] API Failed:", response.status, errorData);

        // Show error in history
        setHistory((prev) => [
          {
            id: crypto.randomUUID(),
            type: "vision",
            status: "failed",
            timestamp: new Date().toISOString(),
            source: `API Error: ${response.status} - ${errorData.error || "Check server logs"}`,
          },
          ...prev,
        ]);

        alert(
          `AI Analysis failed: ${errorData.error || "Server error"}. Check console for details.`,
        );
      }
    } catch (err) {
      // Network error - show to user
      const errorMessage = err instanceof Error ? err.message : "Network error";
      console.error("[Analysis] Network error:", errorMessage);

      setHistory((prev) => [
        {
          id: crypto.randomUUID(),
          type: "vision",
          status: "failed",
          timestamp: new Date().toISOString(),
          source: `Network Error: ${errorMessage}`,
        },
        ...prev,
      ]);

      alert(
        `AI Analysis failed: ${errorMessage}. Check your network connection.`,
      );

      // Remove the old demo fallback logic - we want real errors to be visible
      if (false) {
        // Disabled - keeping for reference
        console.log("[Analysis] Using demo analysis data (fallback)");
        const demoAnalysis = getDemoAnalysis(product!);

        setVision(demoAnalysis.vision);
        setPricing(demoAnalysis.pricing);
        setSimilar(demoAnalysis.similar);
      } else {
        console.error("[Analysis] Error:", err);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Delete product
  const deleteProduct = async () => {
    if (!product) return;

    setDeleting(true);
    try {
      const response = await fetch(`/api/products/${product.id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        // Navigate back to products list
        window.location.href = "/products";
      } else {
        const data = await response.json();
        console.error("[Delete] Failed:", data);
        alert("Failed to delete product: " + (data.error || "Unknown error"));
      }
    } catch (err) {
      console.error("[Delete] Error:", err);
      alert("Failed to delete product");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div
          className="h-8 w-48 rounded"
          style={{ backgroundColor: "#FAFAFA" }}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="h-96" style={{ backgroundColor: "#FAFAFA" }} />
          <div
            className="lg:col-span-2 h-96"
            style={{ backgroundColor: "#FAFAFA" }}
          />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center py-16">
        <p className="text-xl mb-4" style={{ color: "#FF4C00" }}>
          {error || "Product not found"}
        </p>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  const imageUrl = product.image_url || product.thumbnail_url;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2 text-sm"
        style={{ color: "#A9A9A9" }}
      >
        <Link to="/products" className="hover:underline">
          Products
        </Link>
        <span>/</span>
        <span style={{ color: "#171717" }}>{product.title}</span>
      </div>

      {/* Enhanced Header with Image and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Product Image - Larger and more prominent */}
        <div className="lg:col-span-1">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.title}
              className="w-full aspect-square object-cover shadow-lg"
            />
          ) : (
            <div
              className="w-full aspect-square flex items-center justify-center"
              style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
            >
              <span className="text-lg">No Image</span>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="lg:col-span-3 space-y-4">
          {/* Title and Actions Row */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-light" style={{ color: "#171717" }}>
                {product.title}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                {product.brand && (
                  <span
                    className="text-sm font-medium px-3 py-1 rounded-full"
                    style={{ backgroundColor: "#171717", color: "#fff" }}
                  >
                    {product.brand}
                  </span>
                )}
                {product.category && (
                  <span
                    className="text-sm px-3 py-1 rounded-full"
                    style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
                  >
                    {product.category}
                  </span>
                )}
                <StatusBadge status={product.status} />
                <EmbeddingBadge status={product.embedding_status} />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={runAnalysis}
                disabled={analyzing || !imageUrl}
                className="btn-primary text-sm py-2 px-4 flex items-center gap-2 disabled:opacity-50"
              >
                {analyzing ? (
                  <>
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Analyzing
                  </>
                ) : (
                  "Analyze"
                )}
              </button>
              <Link
                to="/products"
                search={{ edit: product.id }}
                className="btn-secondary text-sm py-2 px-4"
              >
                Edit
              </Link>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-sm py-2 px-4 rounded-full border transition-colors hover:bg-penta-coral hover:text-white hover:border-penta-coral"
                style={{
                  borderColor: "var(--penta-coral)",
                  color: "var(--penta-coral)",
                }}
              >
                Delete
              </button>
            </div>
          </div>

          {/* Price and SKU */}
          <div className="flex items-center gap-6">
            {product.price ? (
              <div>
                <p
                  className="text-xs uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Retail Price
                </p>
                <p
                  className="text-2xl font-medium"
                  style={{ color: "#00FBA9" }}
                >
                  {product.currency === "GBP"
                    ? "£"
                    : product.currency === "EUR"
                      ? "€"
                      : "$"}
                  {product.price.toFixed(2)}
                </p>
              </div>
            ) : null}
            {pricing?.minPrice && pricing?.maxPrice && (
              <div>
                <p
                  className="text-xs uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Resale Range
                </p>
                <p
                  className="text-2xl font-medium"
                  style={{ color: "#171717" }}
                >
                  ${pricing.minPrice.toFixed(0)} - $
                  {pricing.maxPrice.toFixed(0)}
                </p>
              </div>
            )}
            {product.sku && (
              <div>
                <p
                  className="text-xs uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  SKU
                </p>
                <p className="font-mono text-sm" style={{ color: "#171717" }}>
                  {product.sku}
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div>
              <p className="text-sm leading-relaxed" style={{ color: "#666" }}>
                {product.description}
              </p>
            </div>
          )}

          {/* Quick Stats */}
          <div className="flex gap-4 pt-2">
            {vision && (
              <span
                className="pill"
                style={{
                  backgroundColor: "rgba(0,251,169,0.15)",
                  color: "var(--penta-mint)",
                }}
              >
                Vision Analyzed
              </span>
            )}
            {pricing && (
              <span
                className="pill"
                style={{
                  backgroundColor: "rgba(57,65,235,0.15)",
                  color: "var(--penta-blue)",
                }}
              >
                {pricing.totalDataPoints || pricing.listings?.length || 0}{" "}
                Listings
              </span>
            )}
            {similar.length > 0 && (
              <span className="pill">{similar.length} Similar</span>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        className="flex gap-1 border-b"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        {(
          [
            "overview",
            "vision",
            "semantic",
            "pricing",
            "similar",
            "history",
          ] as const
        ).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="px-4 py-2 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === tab ? "#171717" : "#A9A9A9",
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {activeTab === tab && (
              <div
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ backgroundColor: "#00FBA9" }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Basic Info */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="card">
            <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
              Product Info
            </h3>
            <div className="space-y-3 text-sm">
              <InfoRow label="Category" value={product.category} />
              <InfoRow
                label="Price"
                value={
                  product.price
                    ? `${product.currency} ${product.price.toLocaleString()}`
                    : null
                }
              />
              <InfoRow
                label="Created"
                value={new Date(product.created_at).toLocaleDateString()}
              />
              <InfoRow
                label="Updated"
                value={new Date(product.updated_at).toLocaleDateString()}
              />
            </div>
            {product.description && (
              <div
                className="mt-4 pt-4 border-t"
                style={{ borderColor: "rgba(169,169,169,0.2)" }}
              >
                <p className="text-sm" style={{ color: "#A9A9A9" }}>
                  {product.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - AI Intelligence */}
        <div className="lg:col-span-2 space-y-6">
          {activeTab === "overview" && (
            <>
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <StatCard
                  label="Vision"
                  value={vision ? "Analyzed" : "Pending"}
                  status={vision ? "success" : "pending"}
                />
                <StatCard
                  label="Pricing"
                  value={
                    pricing?.minPrice
                      ? `$${pricing.minPrice.toFixed(0)} - $${pricing.maxPrice?.toFixed(0)}`
                      : "Pending"
                  }
                  status={pricing?.minPrice ? "success" : "pending"}
                />
                <StatCard
                  label="Embeddings"
                  value={
                    product.embedding_status === "complete"
                      ? "Indexed"
                      : product.embedding_status
                  }
                  status={
                    product.embedding_status === "complete"
                      ? "success"
                      : "pending"
                  }
                />
              </div>

              {/* AI Insights Summary */}
              {(vision || pricing) && (
                <div className="card">
                  <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
                    AI Insights
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {vision && (
                      <div className="space-y-2">
                        <p
                          className="text-xs uppercase tracking-wider"
                          style={{ color: "#A9A9A9" }}
                        >
                          Vision Analysis
                        </p>
                        {vision.brand && (
                          <p className="text-sm">
                            <span style={{ color: "#A9A9A9" }}>Brand:</span>{" "}
                            <span style={{ color: "#171717" }}>
                              {vision.brand}
                            </span>
                          </p>
                        )}
                        {vision.productType && (
                          <p className="text-sm">
                            <span style={{ color: "#A9A9A9" }}>Type:</span>{" "}
                            <span style={{ color: "#171717" }}>
                              {vision.productType}
                            </span>
                          </p>
                        )}
                        {vision.condition && (
                          <p className="text-sm">
                            <span style={{ color: "#A9A9A9" }}>Condition:</span>{" "}
                            <span style={{ color: "#00FBA9" }}>
                              {vision.condition}/10
                            </span>
                          </p>
                        )}
                        {vision.colors && (
                          <p className="text-sm">
                            <span style={{ color: "#A9A9A9" }}>Colors:</span>{" "}
                            <span style={{ color: "#171717" }}>
                              {vision.colors.join(", ")}
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                    {pricing && (
                      <div className="space-y-2">
                        <p
                          className="text-xs uppercase tracking-wider"
                          style={{ color: "#A9A9A9" }}
                        >
                          Pricing Intelligence
                        </p>
                        {pricing.minPrice && (
                          <p
                            className="text-2xl font-light"
                            style={{ color: "#00FBA9" }}
                          >
                            ${pricing.minPrice.toFixed(0)} - $
                            {pricing.maxPrice?.toFixed(0)}
                          </p>
                        )}
                        {pricing.avgPrice && (
                          <p className="text-sm" style={{ color: "#A9A9A9" }}>
                            Avg: $
                            {typeof pricing.avgPrice === "number"
                              ? pricing.avgPrice.toFixed(2)
                              : pricing.avgPrice}
                          </p>
                        )}
                        {pricing.priceRange && (
                          <p className="text-sm" style={{ color: "#A9A9A9" }}>
                            Range: ${pricing.priceRange.min} - $
                            {pricing.priceRange.max}
                          </p>
                        )}
                        {pricing.confidence && (
                          <p className="text-sm" style={{ color: "#A9A9A9" }}>
                            Confidence: {Math.round(pricing.confidence * 100)}%
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Valuation Card */}
              {valuation &&
                (valuation.quickSale ||
                  valuation.fairMarket ||
                  valuation.premium) && (
                  <div className="card">
                    <h3
                      className="font-medium mb-4"
                      style={{ color: "#171717" }}
                    >
                      AI Valuation
                      <span
                        className="text-xs px-2 py-0.5 rounded ml-2"
                        style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
                      >
                        via dgx-gateway
                      </span>
                    </h3>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {valuation.quickSale && (
                        <div
                          className="text-center p-4"
                          style={{ backgroundColor: "rgba(255,76,0,0.1)" }}
                        >
                          <p
                            className="text-xs uppercase tracking-wider mb-1"
                            style={{ color: "#A9A9A9" }}
                          >
                            Quick Sale
                          </p>
                          <p
                            className="text-2xl font-medium"
                            style={{ color: "#FF4C00" }}
                          >
                            £{valuation.quickSale.gbp}
                          </p>
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            {Math.round(valuation.quickSale.confidence * 100)}%
                            confidence
                          </p>
                        </div>
                      )}
                      {valuation.fairMarket && (
                        <div
                          className="text-center p-4"
                          style={{ backgroundColor: "rgba(0,251,169,0.1)" }}
                        >
                          <p
                            className="text-xs uppercase tracking-wider mb-1"
                            style={{ color: "#A9A9A9" }}
                          >
                            Fair Market
                          </p>
                          <p
                            className="text-2xl font-medium"
                            style={{ color: "#00FBA9" }}
                          >
                            £{valuation.fairMarket.gbp}
                          </p>
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            {Math.round(valuation.fairMarket.confidence * 100)}%
                            confidence
                          </p>
                        </div>
                      )}
                      {valuation.premium && (
                        <div
                          className="text-center p-4"
                          style={{ backgroundColor: "rgba(57,65,235,0.1)" }}
                        >
                          <p
                            className="text-xs uppercase tracking-wider mb-1"
                            style={{ color: "#A9A9A9" }}
                          >
                            Premium
                          </p>
                          <p
                            className="text-2xl font-medium"
                            style={{ color: "#3941EB" }}
                          >
                            £{valuation.premium.gbp}
                          </p>
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            {Math.round(valuation.premium.confidence * 100)}%
                            confidence
                          </p>
                        </div>
                      )}
                    </div>
                    {valuation.marketContext && (
                      <div className="flex gap-4 mb-3">
                        {valuation.marketContext.demand && (
                          <span
                            className="text-xs px-2 py-1 rounded"
                            style={{ backgroundColor: "#FAFAFA" }}
                          >
                            Demand:{" "}
                            <span style={{ color: "#171717" }}>
                              {valuation.marketContext.demand}
                            </span>
                          </span>
                        )}
                        {valuation.marketContext.supply && (
                          <span
                            className="text-xs px-2 py-1 rounded"
                            style={{ backgroundColor: "#FAFAFA" }}
                          >
                            Supply:{" "}
                            <span style={{ color: "#171717" }}>
                              {valuation.marketContext.supply}
                            </span>
                          </span>
                        )}
                        {valuation.marketContext.trend && (
                          <span
                            className="text-xs px-2 py-1 rounded"
                            style={{ backgroundColor: "#FAFAFA" }}
                          >
                            Trend:{" "}
                            <span style={{ color: "#171717" }}>
                              {valuation.marketContext.trend}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                    {valuation.reasoning && (
                      <p className="text-sm" style={{ color: "#666" }}>
                        {valuation.reasoning}
                      </p>
                    )}
                  </div>
                )}

              {/* CLM Enrichment */}
              {clm && clm.taxonomyId && (
                <div className="card">
                  <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
                    Semantic Enrichment
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p
                        className="text-xs uppercase tracking-wider mb-1"
                        style={{ color: "#A9A9A9" }}
                      >
                        Taxonomy
                      </p>
                      <p
                        className="text-sm font-mono"
                        style={{ color: "#171717" }}
                      >
                        {clm.taxonomyId}
                      </p>
                    </div>
                    {clm.searchStrategy?.primary_query && (
                      <div>
                        <p
                          className="text-xs uppercase tracking-wider mb-1"
                          style={{ color: "#A9A9A9" }}
                        >
                          Search Query
                        </p>
                        <p className="text-sm" style={{ color: "#171717" }}>
                          {clm.searchStrategy.primary_query}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Similar Products Preview */}
              {similar.length > 0 && (
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium" style={{ color: "#171717" }}>
                      Similar Products
                    </h3>
                    <button
                      onClick={() => setActiveTab("similar")}
                      className="text-sm"
                      style={{ color: "#00FBA9" }}
                    >
                      View All →
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {similar.slice(0, 3).map((item) => (
                      <SimilarProductCard key={item.id} product={item} />
                    ))}
                  </div>
                </div>
              )}

              {/* No Analysis Yet */}
              {!vision && !pricing && similar.length === 0 && (
                <div
                  className="card text-center py-12"
                  style={{ backgroundColor: "rgba(0,251,169,0.05)" }}
                >
                  <h3 className="font-medium mb-2" style={{ color: "#171717" }}>
                    No AI Analysis Yet
                  </h3>
                  <p className="text-sm mb-4" style={{ color: "#A9A9A9" }}>
                    Run AI analysis to get vision insights, pricing
                    intelligence, and find similar products
                  </p>
                  <button
                    onClick={runAnalysis}
                    disabled={analyzing || !imageUrl}
                    className="btn-primary text-sm"
                  >
                    {analyzing ? "Analyzing" : "Analyze"}
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === "vision" && (
            <div className="card">
              <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
                Vision Analysis
                {vision?.source && (
                  <span
                    className="text-xs px-2 py-0.5 rounded"
                    style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
                  >
                    via {vision.source}
                  </span>
                )}
              </h3>
              {vision ? (
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <VisionField label="Brand" value={vision.brand} />
                    <VisionField
                      label="Product Type"
                      value={vision.productType}
                    />
                    <VisionField label="Category" value={vision.category} />
                    <VisionField label="Material" value={vision.material} />
                  </div>
                  <div className="space-y-4">
                    <VisionField
                      label="Condition"
                      value={
                        vision.condition ? `${vision.condition}/10` : undefined
                      }
                      highlight
                    />
                    <VisionField
                      label="Colors"
                      value={vision.colors?.join(", ")}
                    />
                    <VisionField
                      label="Est. Retail"
                      value={
                        vision.estimatedRetailPrice
                          ? `$${vision.estimatedRetailPrice}`
                          : undefined
                      }
                    />
                  </div>
                  {vision.description && (
                    <div
                      className="col-span-2 pt-4 border-t"
                      style={{ borderColor: "rgba(169,169,169,0.2)" }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-2"
                        style={{ color: "#A9A9A9" }}
                      >
                        AI Description
                      </p>
                      <p className="text-sm" style={{ color: "#171717" }}>
                        {vision.description}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState message="Run AI analysis to see vision results" />
              )}
            </div>
          )}

          {activeTab === "semantic" && (
            <div className="card">
              <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
                Semantic Intelligence
                <span
                  className="text-xs px-2 py-0.5 rounded ml-2"
                  style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
                >
                  LLM-Powered Search Context
                </span>
              </h3>
              {clm &&
              (clm.idealFor?.length ||
                clm.pairsWellWith?.length ||
                clm.searchKeywords?.length) ? (
                <div className="space-y-6">
                  {/* Enriched Description */}
                  {clm.enrichedDescription && (
                    <div
                      className="p-4"
                      style={{ backgroundColor: "rgba(0,251,169,0.1)" }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-2"
                        style={{ color: "#00FBA9" }}
                      >
                        AI-Enhanced Description
                      </p>
                      <p className="text-sm" style={{ color: "#171717" }}>
                        {clm.enrichedDescription}
                      </p>
                    </div>
                  )}

                  {/* Two Column Layout */}
                  <div className="grid grid-cols-2 gap-6">
                    {/* Ideal For - Use Cases */}
                    {clm.idealFor && clm.idealFor.length > 0 && (
                      <div>
                        <p
                          className="text-xs uppercase tracking-wider mb-3"
                          style={{ color: "#A9A9A9" }}
                        >
                          Ideal For
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {clm.idealFor.map((use, i) => (
                            <span
                              key={i}
                              className="px-3 py-1.5 rounded-full text-xs"
                              style={{
                                backgroundColor: "rgba(0,251,169,0.15)",
                                color: "#00FBA9",
                              }}
                            >
                              {use}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Not Ideal For - Limitations */}
                    {clm.notIdealFor && clm.notIdealFor.length > 0 && (
                      <div>
                        <p
                          className="text-xs uppercase tracking-wider mb-3"
                          style={{ color: "#A9A9A9" }}
                        >
                          Not Ideal For
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {clm.notIdealFor.map((use, i) => (
                            <span
                              key={i}
                              className="px-3 py-1.5 rounded-full text-xs"
                              style={{
                                backgroundColor: "rgba(255,76,0,0.15)",
                                color: "#FF4C00",
                              }}
                            >
                              {use}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pairs Well With */}
                  {clm.pairsWellWith && clm.pairsWellWith.length > 0 && (
                    <div>
                      <p
                        className="text-xs uppercase tracking-wider mb-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        Pairs Well With
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {clm.pairsWellWith.map((item, i) => (
                          <span
                            key={i}
                            className="px-3 py-1.5 rounded-full text-xs border"
                            style={{
                              borderColor: "rgba(57,65,235,0.3)",
                              color: "#3941EB",
                              backgroundColor: "rgba(57,65,235,0.1)",
                            }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Features */}
                  {clm.keyFeatures && clm.keyFeatures.length > 0 && (
                    <div>
                      <p
                        className="text-xs uppercase tracking-wider mb-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        Key Features
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {clm.keyFeatures.map((feature, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 text-sm"
                            style={{ color: "#171717" }}
                          >
                            <span style={{ color: "#00FBA9" }}>•</span>
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* User Contexts & Style */}
                  <div className="grid grid-cols-2 gap-6">
                    {clm.userContexts && clm.userContexts.length > 0 && (
                      <div>
                        <p
                          className="text-xs uppercase tracking-wider mb-3"
                          style={{ color: "#A9A9A9" }}
                        >
                          Perfect For
                        </p>
                        <div className="space-y-2">
                          {clm.userContexts.map((ctx, i) => (
                            <div
                              key={i}
                              className="text-sm"
                              style={{ color: "#171717" }}
                            >
                              {ctx}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {clm.styleDescriptors &&
                        clm.styleDescriptors.length > 0 && (
                          <div>
                            <p
                              className="text-xs uppercase tracking-wider mb-2"
                              style={{ color: "#A9A9A9" }}
                            >
                              Style
                            </p>
                            <p className="text-sm" style={{ color: "#171717" }}>
                              {clm.styleDescriptors.join(" • ")}
                            </p>
                          </div>
                        )}
                      {clm.qualityTier && (
                        <div>
                          <p
                            className="text-xs uppercase tracking-wider mb-2"
                            style={{ color: "#A9A9A9" }}
                          >
                            Quality Tier
                          </p>
                          <span
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor:
                                clm.qualityTier === "luxury"
                                  ? "rgba(57,65,235,0.15)"
                                  : clm.qualityTier === "premium"
                                    ? "rgba(0,251,169,0.15)"
                                    : "#FAFAFA",
                              color:
                                clm.qualityTier === "luxury"
                                  ? "#3941EB"
                                  : clm.qualityTier === "premium"
                                    ? "#00FBA9"
                                    : "#171717",
                            }}
                          >
                            {clm.qualityTier}
                          </span>
                        </div>
                      )}
                      {clm.seasonalRelevance && (
                        <div>
                          <p
                            className="text-xs uppercase tracking-wider mb-2"
                            style={{ color: "#A9A9A9" }}
                          >
                            Seasonal
                          </p>
                          <p className="text-sm" style={{ color: "#171717" }}>
                            {clm.seasonalRelevance}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Keywords - The LLM Discovery Section */}
                  {clm.searchKeywords && clm.searchKeywords.length > 0 && (
                    <div
                      className="p-4"
                      style={{
                        backgroundColor: "rgba(57,65,235,0.05)",
                        borderLeft: "3px solid #3941EB",
                      }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-3"
                        style={{ color: "#3941EB" }}
                      >
                        LLM Search Keywords
                      </p>
                      <p
                        className="text-xs mb-3"
                        style={{ color: "#A9A9A9", fontStyle: "italic" }}
                      >
                        Natural language queries that should surface this
                        product:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {clm.searchKeywords.map((keyword, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 rounded text-xs"
                            style={{
                              backgroundColor: "#fff",
                              color: "#171717",
                              border: "1px solid rgba(169,169,169,0.3)",
                            }}
                          >
                            "{keyword}"
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Care Considerations */}
                  {clm.careConsiderations &&
                    clm.careConsiderations.length > 0 && (
                      <div>
                        <p
                          className="text-xs uppercase tracking-wider mb-3"
                          style={{ color: "#A9A9A9" }}
                        >
                          Care Tips
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {clm.careConsiderations.map((tip, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 rounded text-xs"
                              style={{
                                backgroundColor: "#FAFAFA",
                                color: "#666",
                              }}
                            >
                              {tip}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Legacy: Taxonomy & Search Strategy */}
                  <div
                    className="pt-4 border-t"
                    style={{ borderColor: "rgba(169,169,169,0.2)" }}
                  >
                    <div className="grid grid-cols-2 gap-6">
                      {clm.taxonomyId && (
                        <div>
                          <p
                            className="text-xs uppercase tracking-wider mb-1"
                            style={{ color: "#A9A9A9" }}
                          >
                            Taxonomy ID
                          </p>
                          <p
                            className="text-sm font-mono"
                            style={{ color: "#171717" }}
                          >
                            {clm.taxonomyId}
                          </p>
                        </div>
                      )}
                      {clm.searchStrategy?.primary_query && (
                        <div>
                          <p
                            className="text-xs uppercase tracking-wider mb-1"
                            style={{ color: "#A9A9A9" }}
                          >
                            Primary Search Query
                          </p>
                          <p className="text-sm" style={{ color: "#171717" }}>
                            {clm.searchStrategy.primary_query}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Show minimal CLM data if available */}
                  {clm && clm.taxonomyId && (
                    <div className="space-y-3">
                      <div>
                        <p
                          className="text-xs uppercase tracking-wider mb-1"
                          style={{ color: "#A9A9A9" }}
                        >
                          Taxonomy
                        </p>
                        <p
                          className="text-sm font-mono"
                          style={{ color: "#171717" }}
                        >
                          {clm.taxonomyId}
                        </p>
                      </div>
                      {clm.searchStrategy?.primary_query && (
                        <div>
                          <p
                            className="text-xs uppercase tracking-wider mb-1"
                            style={{ color: "#A9A9A9" }}
                          >
                            Search Query
                          </p>
                          <p className="text-sm" style={{ color: "#171717" }}>
                            {clm.searchStrategy.primary_query}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  <EmptyState message="Run AI analysis to get rich semantic enrichment - ideal use cases, pairing suggestions, and LLM search keywords" />
                </div>
              )}
            </div>
          )}

          {activeTab === "pricing" && (
            <div className="card">
              <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
                Marketplace Intelligence
              </h3>
              {pricing ? (
                <div className="space-y-6">
                  {/* Price Range Hero */}
                  <div
                    className="text-center p-6"
                    style={{ backgroundColor: "rgba(0,251,169,0.1)" }}
                  >
                    <p
                      className="text-xs uppercase tracking-wider mb-2"
                      style={{ color: "#A9A9A9" }}
                    >
                      Resale Price Range
                    </p>
                    <p
                      className="text-4xl font-light"
                      style={{ color: "#00FBA9" }}
                    >
                      ${pricing.minPrice?.toFixed(2) || "—"} - $
                      {pricing.maxPrice?.toFixed(2) || "—"}
                    </p>
                    {pricing.avgPrice && (
                      <p className="text-sm mt-2" style={{ color: "#A9A9A9" }}>
                        Average: $
                        {typeof pricing.avgPrice === "number"
                          ? pricing.avgPrice.toFixed(2)
                          : pricing.avgPrice}
                      </p>
                    )}
                  </div>

                  {/* Volume & Confidence Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <div
                      className="text-center p-4"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-1"
                        style={{ color: "#A9A9A9" }}
                      >
                        Listings Found
                      </p>
                      <p
                        className="text-2xl font-medium"
                        style={{ color: "#171717" }}
                      >
                        {pricing.totalDataPoints || 0}
                      </p>
                    </div>
                    <div
                      className="text-center p-4"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-1"
                        style={{ color: "#A9A9A9" }}
                      >
                        Amazon
                      </p>
                      <p
                        className="text-2xl font-medium"
                        style={{ color: "#FF9900" }}
                      >
                        {pricing.amazonListings || 0}
                      </p>
                    </div>
                    <div
                      className="text-center p-4"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-1"
                        style={{ color: "#A9A9A9" }}
                      >
                        eBay
                      </p>
                      <p
                        className="text-2xl font-medium"
                        style={{ color: "#0064D2" }}
                      >
                        {pricing.ebayListings || 0}
                      </p>
                    </div>
                    <div
                      className="text-center p-4"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-1"
                        style={{ color: "#A9A9A9" }}
                      >
                        Confidence
                      </p>
                      <p
                        className="text-2xl font-medium"
                        style={{ color: "#171717" }}
                      >
                        {pricing.confidence
                          ? `${Math.round(pricing.confidence * 100)}%`
                          : "—"}
                      </p>
                    </div>
                  </div>

                  {/* Market Insight Card */}
                  {pricing.marketInsight && (
                    <div
                      className="p-4 border-l-4"
                      style={{
                        backgroundColor: "#FAFAFA",
                        borderColor:
                          pricing.marketInsight.volumeLevel === "very_high" ||
                          pricing.marketInsight.volumeLevel === "high"
                            ? "#00FBA9"
                            : pricing.marketInsight.volumeLevel === "medium"
                              ? "#FFA500"
                              : "#A9A9A9",
                      }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-2"
                        style={{ color: "#A9A9A9" }}
                      >
                        Market Insight
                      </p>
                      <p className="text-sm mb-3" style={{ color: "#171717" }}>
                        {pricing.marketInsight.summary}
                      </p>
                      <div
                        className="p-3 rounded"
                        style={{ backgroundColor: "rgba(0,251,169,0.1)" }}
                      >
                        <p
                          className="text-xs uppercase tracking-wider mb-1"
                          style={{ color: "#00FBA9" }}
                        >
                          Buyback Recommendation
                        </p>
                        <p
                          className="text-sm font-medium"
                          style={{ color: "#171717" }}
                        >
                          {pricing.marketInsight.recommendation}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Marketplaces Scraped */}
                  {pricing.marketplacesScraped &&
                    pricing.marketplacesScraped.length > 0 && (
                      <div>
                        <p
                          className="text-xs uppercase tracking-wider mb-3"
                          style={{ color: "#A9A9A9" }}
                        >
                          Marketplaces Checked
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {pricing.marketplacesScraped.map((mp, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: "#FAFAFA",
                                color: "#171717",
                                border: "1px solid rgba(169,169,169,0.3)",
                              }}
                            >
                              {mp}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Data Sources */}
                  {pricing.sourcesUsed && pricing.sourcesUsed.length > 0 && (
                    <div>
                      <p
                        className="text-xs uppercase tracking-wider mb-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        Data Sources
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {pricing.sourcesUsed.map((src, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 rounded text-xs"
                            style={{
                              backgroundColor: src.includes("amazon")
                                ? "rgba(255,153,0,0.1)"
                                : src.includes("ebay")
                                  ? "rgba(0,100,210,0.1)"
                                  : src.includes("stockx")
                                    ? "rgba(0,182,122,0.1)"
                                    : "#FAFAFA",
                              color: "#171717",
                            }}
                          >
                            {src}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Explanation */}
                  {pricing.explanation && (
                    <div
                      className="p-4 rounded"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <p
                        className="text-xs uppercase tracking-wider mb-2"
                        style={{ color: "#A9A9A9" }}
                      >
                        Analysis Details
                      </p>
                      <p className="text-sm" style={{ color: "#171717" }}>
                        {pricing.explanation}
                      </p>
                    </div>
                  )}

                  {/* Listings Table - Show individual products found */}
                  {pricing.listings && pricing.listings.length > 0 && (
                    <div>
                      <p
                        className="text-xs uppercase tracking-wider mb-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        Listings Found ({pricing.listings.length})
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ backgroundColor: "#FAFAFA" }}>
                              <th
                                className="text-left p-3 font-medium"
                                style={{ color: "#A9A9A9" }}
                              >
                                Product
                              </th>
                              <th
                                className="text-left p-3 font-medium"
                                style={{ color: "#A9A9A9" }}
                              >
                                Condition
                              </th>
                              <th
                                className="text-right p-3 font-medium"
                                style={{ color: "#A9A9A9" }}
                              >
                                Price
                              </th>
                              <th
                                className="text-center p-3 font-medium"
                                style={{ color: "#A9A9A9" }}
                              >
                                Source
                              </th>
                              <th
                                className="text-center p-3 font-medium"
                                style={{ color: "#A9A9A9" }}
                              >
                                Link
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pricing.listings.map((listing, i) => (
                              <tr
                                key={i}
                                className="border-b transition-colors hover:bg-gray-50"
                                style={{ borderColor: "rgba(169,169,169,0.2)" }}
                              >
                                <td
                                  className="p-3"
                                  style={{ color: "#171717" }}
                                >
                                  <div
                                    className="max-w-xs truncate"
                                    title={listing.title}
                                  >
                                    {listing.title}
                                  </div>
                                </td>
                                <td className="p-3">
                                  <span
                                    className="px-2 py-0.5 rounded text-xs"
                                    style={{
                                      backgroundColor: listing.condition
                                        ?.toLowerCase()
                                        .includes("new")
                                        ? "rgba(0,251,169,0.2)"
                                        : "#FAFAFA",
                                      color: "#171717",
                                    }}
                                  >
                                    {listing.condition || "Used"}
                                  </span>
                                </td>
                                <td
                                  className="p-3 text-right font-medium"
                                  style={{ color: "#00FBA9" }}
                                >
                                  ${listing.price?.toFixed(2)}
                                </td>
                                <td className="p-3 text-center">
                                  <span
                                    className="px-2 py-0.5 rounded text-xs"
                                    style={{
                                      backgroundColor:
                                        listing.source === "eBay"
                                          ? "rgba(0,100,210,0.1)"
                                          : listing.source === "Amazon"
                                            ? "rgba(255,153,0,0.1)"
                                            : "#FAFAFA",
                                      color:
                                        listing.source === "eBay"
                                          ? "#0064D2"
                                          : listing.source === "Amazon"
                                            ? "#FF9900"
                                            : "#171717",
                                    }}
                                  >
                                    {listing.source}
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  {listing.url && (
                                    <a
                                      href={listing.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs px-2 py-1 rounded hover:bg-gray-100"
                                      style={{ color: "#0064D2" }}
                                    >
                                      View →
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Legacy comparables support */}
                  {pricing.comparables && pricing.comparables.length > 0 && (
                    <div>
                      <p
                        className="text-xs uppercase tracking-wider mb-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        Comparable Sales
                      </p>
                      <div className="space-y-2">
                        {pricing.comparables.map((comp, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between p-3 rounded"
                            style={{ backgroundColor: "#FAFAFA" }}
                          >
                            <span
                              className="text-sm"
                              style={{ color: "#171717" }}
                            >
                              {comp.title}
                            </span>
                            <span
                              className="font-medium"
                              style={{ color: "#00FBA9" }}
                            >
                              ${comp.price}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState message="Run AI analysis to get pricing intelligence" />
              )}
            </div>
          )}

          {activeTab === "similar" && (
            <div className="card">
              <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
                Similar Products
                <span
                  className="text-xs px-2 py-0.5 rounded"
                  style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
                >
                  via Milvus
                </span>
              </h3>
              {similar.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {similar.map((item) => (
                    <SimilarProductCard key={item.id} product={item} detailed />
                  ))}
                </div>
              ) : (
                <EmptyState message="Run AI analysis to find similar products" />
              )}
            </div>
          )}

          {activeTab === "history" && (
            <div className="card">
              <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
                Enrichment History
              </h3>
              {history.length > 0 ? (
                <div className="space-y-3">
                  {history.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-3 rounded"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono"
                          style={{
                            backgroundColor:
                              entry.type === "vision"
                                ? "rgba(0,251,169,0.15)"
                                : entry.type === "pricing"
                                  ? "rgba(57,65,235,0.15)"
                                  : "rgba(169,169,169,0.15)",
                            color:
                              entry.type === "vision"
                                ? "var(--penta-mint)"
                                : entry.type === "pricing"
                                  ? "var(--penta-blue)"
                                  : "var(--penta-grey-medium)",
                          }}
                        >
                          {entry.type.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p
                            className="text-sm font-medium"
                            style={{ color: "#171717" }}
                          >
                            {entry.type.charAt(0).toUpperCase() +
                              entry.type.slice(1)}{" "}
                            Analysis
                          </p>
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            via {entry.source} •{" "}
                            {new Date(entry.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <span
                        className="px-2 py-1 rounded text-xs"
                        style={{
                          backgroundColor:
                            entry.status === "success"
                              ? "rgba(0,251,169,0.15)"
                              : "rgba(255,76,0,0.15)",
                          color:
                            entry.status === "success" ? "#00FBA9" : "#FF4C00",
                        }}
                      >
                        {entry.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No enrichment history yet" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-white p-6 max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-xl font-medium mb-2"
              style={{ color: "#171717" }}
            >
              Delete Product?
            </h3>
            <p className="text-sm mb-6" style={{ color: "#A9A9A9" }}>
              Are you sure you want to delete "{product.title}"? This action
              cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-full font-medium"
                style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
              >
                Cancel
              </button>
              <button
                onClick={deleteProduct}
                disabled={deleting}
                className="px-4 py-2 rounded-full font-medium flex items-center gap-2"
                style={{ backgroundColor: "#FF4C00", color: "#fff" }}
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components
function InfoRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="flex justify-between">
      <span style={{ color: "#A9A9A9" }}>{label}</span>
      <span style={{ color: "#171717" }}>{value || "—"}</span>
    </div>
  );
}

function StatCard({
  label,
  value,
  status,
}: {
  label: string;
  value: string;
  status: "success" | "pending" | "failed";
}) {
  const colors = {
    success: { bg: "rgba(0,251,169,0.1)", text: "#00FBA9" },
    pending: { bg: "rgba(169,169,169,0.1)", text: "#A9A9A9" },
    failed: { bg: "rgba(255,76,0,0.1)", text: "#FF4C00" },
  };

  return (
    <div
      className="card text-center py-4"
      style={{ backgroundColor: colors[status].bg }}
    >
      <p
        className="text-xs uppercase tracking-wider font-mono"
        style={{ color: "#A9A9A9" }}
      >
        {label}
      </p>
      <p
        className="font-medium mt-1 text-sm"
        style={{ color: colors[status].text }}
      >
        {value}
      </p>
    </div>
  );
}

function VisionField({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value?: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <p
        className="text-xs uppercase tracking-wider mb-1"
        style={{ color: "#A9A9A9" }}
      >
        {label}
      </p>
      <p
        className="text-sm"
        style={{ color: highlight && value ? "#00FBA9" : "#171717" }}
      >
        {value || "—"}
      </p>
    </div>
  );
}

function SimilarProductCard({
  product,
  detailed = false,
}: {
  product: SimilarProduct;
  detailed?: boolean;
}) {
  return (
    <div
      className="overflow-hidden border transition-shadow hover:shadow-md"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      {product.image_url ? (
        <img
          src={product.image_url}
          alt={product.title}
          className={`w-full object-cover ${detailed ? "h-32" : "h-24"}`}
          style={{ backgroundColor: "#FAFAFA" }}
        />
      ) : (
        <div
          className={`w-full flex items-center justify-center ${detailed ? "h-32" : "h-24"}`}
          style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
        >
          No img
        </div>
      )}
      <div className="p-2">
        <p className="text-xs truncate" style={{ color: "#171717" }}>
          {product.title}
        </p>
        {detailed && product.brand && (
          <p className="text-xs" style={{ color: "#A9A9A9" }}>
            {product.brand}
          </p>
        )}
        <div className="flex items-center justify-between mt-1">
          {product.price && (
            <span className="text-xs font-medium" style={{ color: "#00FBA9" }}>
              ${product.price}
            </span>
          )}
          <span className="text-xs" style={{ color: "#A9A9A9" }}>
            {Math.round(product.score * 100)}% match
          </span>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-8">
      <p className="text-sm" style={{ color: "#A9A9A9" }}>
        {message}
      </p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string }> = {
    active: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9" },
    draft: { bg: "rgba(169,169,169,0.15)", text: "#A9A9A9" },
    archived: { bg: "rgba(255,76,0,0.15)", text: "#FF4C00" },
  };
  const style = styles[status] || styles.draft;
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-mono uppercase"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {status}
    </span>
  );
}

function EmbeddingBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string; label: string }> = {
    complete: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9", label: "Indexed" },
    processing: {
      bg: "rgba(57,65,235,0.15)",
      text: "#3941EB",
      label: "Processing",
    },
    pending: {
      bg: "rgba(169,169,169,0.15)",
      text: "#A9A9A9",
      label: "Pending",
    },
    failed: { bg: "rgba(255,76,0,0.15)", text: "#FF4C00", label: "Failed" },
  };
  const style = styles[status] || styles.pending;
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-mono uppercase"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {style.label}
    </span>
  );
}

// Demo analysis data generator
function getDemoAnalysis(product: Product): {
  vision: VisionAnalysis;
  pricing: PricingIntelligence;
  similar: SimilarProduct[];
} {
  // Prices are in dollars (e.g., 600 for $600)
  const price = product.price || 500;
  const resaleMin = Math.round(price * 0.35);
  const resaleMax = Math.round(price * 0.65);
  const resaleAvg = Math.round((resaleMin + resaleMax) / 2);

  // Get category-specific colors
  const categoryColors: Record<string, string[]> = {
    "Shell Jackets": ["Black", "Gore-Tex Black", "Stealth"],
    "Insulated Jackets": ["Pilot", "Labyrinth", "Flux"],
    Fleece: ["Black Heather", "Kingfisher"],
    Pants: ["Black", "Carbon"],
  };

  const colors = categoryColors[product.category || ""] || ["Black"];

  return {
    vision: {
      brand: product.brand || "Arc'teryx",
      category: product.category || "Outerwear",
      productType: product.title,
      condition: 9,
      colors: colors,
      material: product.category?.includes("Shell")
        ? "GORE-TEX Pro"
        : product.category?.includes("Insulated")
          ? "Coreloft™"
          : product.category?.includes("Fleece")
            ? "Polartec®"
            : "Nylon",
      estimatedRetailPrice: price,
      description: `High-quality ${product.brand} ${product.category?.toLowerCase() || "garment"} in excellent condition. Premium construction with technical features.`,
      source: "demo-vision",
    },
    pricing: {
      minPrice: resaleMin,
      maxPrice: resaleMax,
      avgPrice: resaleAvg,
      confidence: 0.87,
      explanation: `Based on analysis of similar ${product.brand} ${product.category} listings across major resale platforms.`,
      searchQuery: `${product.brand} ${product.title}`,
      totalDataPoints: 24,
      amazonListings: 8,
      ebayListings: 12,
      sourcesUsed: ["eBay", "Amazon", "Grailed", "Poshmark"],
      marketplacesScraped: [
        "eBay",
        "Amazon",
        "Grailed",
        "Poshmark",
        "The RealReal",
      ],
      cached: false,
      listings: [
        {
          title: `${product.brand} ${product.title} - Like New`,
          price: resaleMax,
          currency: "USD",
          condition: "Like New",
          source: "eBay",
          url: "https://ebay.com",
        },
        {
          title: `${product.title} ${product.category}`,
          price: resaleAvg,
          currency: "USD",
          condition: "Excellent",
          source: "Grailed",
          url: "https://grailed.com",
        },
        {
          title: `Pre-owned ${product.brand} ${product.title}`,
          price: resaleMin,
          currency: "USD",
          condition: "Good",
          source: "Poshmark",
          url: "https://poshmark.com",
        },
      ],
      marketInsight: {
        summary: `${product.brand} ${product.category} maintains strong resale value due to brand reputation and technical performance. High demand in outdoor enthusiast market.`,
        volumeLevel: "high",
        volume: 24,
        priceSpread: resaleMax - resaleMin,
        avgPrice: `$${resaleAvg.toFixed(2)}`,
        marketplaces: ["eBay", "Grailed", "Poshmark"],
        confidence: "87%",
        recommendation: `Offer $${Math.round(resaleMin * 0.6)}-$${Math.round(resaleMin * 0.8)} for trade-in credit (40-50% of resale value). Expected margin: 50-60%.`,
      },
    },
    similar: [
      {
        id: "sim-1",
        title:
          product.category === "Shell Jackets"
            ? "Beta LT Jacket"
            : "Atom AR Hoody",
        brand: "Arc'teryx",
        price: Math.round(price * 0.85),
        image_url: "/images/products/arcteryx/beta-lt-jacket.jpeg",
        score: 0.94,
      },
      {
        id: "sim-2",
        title:
          product.category === "Shell Jackets"
            ? "Zeta SL Jacket"
            : "Proton LT Hoody",
        brand: "Arc'teryx",
        price: Math.round(price * 0.75),
        image_url: "/images/products/arcteryx/zeta-sl-jacket.jpeg",
        score: 0.89,
      },
      {
        id: "sim-3",
        title: "Cerium LT Jacket",
        brand: "Arc'teryx",
        price: 379,
        image_url: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
        score: 0.82,
      },
    ],
  };
}

// Demo Arc'teryx products for development with realistic retail prices
function getArcteryxDemoProduct(id: string): Product | null {
  const products: Record<string, Product> = {
    "arc-1": {
      id: "arc-1",
      sku: "ARC-25854-BLK-M",
      title: "Beta SL Jacket",
      description:
        "Lightweight, packable GORE-TEX shell for hiking and trekking. Minimalist design with essential weather protection.",
      brand: "Arc'teryx",
      category: "Shell Jackets",
      price: 600,
      currency: "USD",
      image_url: "/images/products/arcteryx/beta-ar-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/beta-ar-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-01T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-2": {
      id: "arc-2",
      sku: "ARC-24108-PILOT-L",
      title: "Konseal Hybrid Hoody",
      description:
        "Lightweight, breathable hybrid hoody with fleece body and stretch panels. Perfect for climbing and high-output activities.",
      brand: "Arc'teryx",
      category: "Insulated Jackets",
      price: 220,
      currency: "USD",
      image_url: "/images/products/arcteryx/atom-lt-hoody.jpeg",
      thumbnail_url: "/images/products/arcteryx/atom-lt-hoody.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-02T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-3": {
      id: "arc-3",
      sku: "ARC-25681-BLACK-M",
      title: "Alpha SV Jacket",
      description:
        "The pinnacle of alpine protection. Built for severe alpine conditions with the most durable GORE-TEX Pro available. The flagship shell for elite alpinists.",
      brand: "Arc'teryx",
      category: "Shell Jackets",
      price: 1100,
      currency: "USD",
      image_url: "/images/products/arcteryx/alpha-sv-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/alpha-sv-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-03T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-4": {
      id: "arc-4",
      sku: "ARC-17225-BLK-32",
      title: "Beta Pant",
      description:
        "Durable, waterproof GORE-TEX pant for all-around mountain use. Articulated patterning for freedom of movement.",
      brand: "Arc'teryx",
      category: "Pants",
      price: 360,
      currency: "USD",
      image_url: "/images/products/arcteryx/gamma-lt-pants.jpeg",
      thumbnail_url: "/images/products/arcteryx/gamma-lt-pants.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-04T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-5": {
      id: "arc-5",
      sku: "ARC-21773-LABYRINTH-S",
      title: "Cerium SV Vest",
      description:
        "Superlight down vest with exceptional warmth-to-weight ratio. Features 850-fill European goose down for core warmth.",
      brand: "Arc'teryx",
      category: "Insulated Jackets",
      price: 420,
      currency: "USD",
      image_url: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-05T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-6": {
      id: "arc-6",
      sku: "ARC-25445-FLUX-M",
      title: "Emblem Fleece Crew",
      description:
        "Comfortable fleece crew neck pullover with Arc'teryx emblem. Polartec® fleece for everyday warmth.",
      brand: "Arc'teryx",
      category: "Fleece",
      price: 130,
      currency: "USD",
      image_url: "/images/products/arcteryx/proton-fl-hoody.jpeg",
      thumbnail_url: "/images/products/arcteryx/proton-fl-hoody.jpeg",
      status: "active",
      embedding_status: "processing",
      attributes_json: null,
      created_at: "2024-12-06T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-7": {
      id: "arc-7",
      sku: "ARC-21776-BLACK-L",
      title: "Kragg Cotton Crew Shirt",
      description:
        "Organic cotton short sleeve crew with Little Bird graphic. Comfortable casual wear with Arc'teryx style.",
      brand: "Arc'teryx",
      category: "Shirts",
      price: 80,
      currency: "USD",
      image_url: "/images/products/arcteryx/zeta-sl-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/zeta-sl-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-07T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-8": {
      id: "arc-8",
      sku: "ARC-12397-BLACK-M",
      title: "Covert Cardigan",
      description:
        "Polartec® fleece cardigan with clean styling for everyday wear. Versatile piece that transitions from trail to town.",
      brand: "Arc'teryx",
      category: "Fleece",
      price: 179,
      currency: "USD",
      image_url: "/images/products/arcteryx/covert-cardigan.jpeg",
      thumbnail_url: "/images/products/arcteryx/covert-cardigan.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-08T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-9": {
      id: "arc-9",
      sku: "ARC-18965-KINGFISHER-S",
      title: "Chunky Ribbed Toque",
      description:
        "Warm, chunky knit toque with ribbed texture. Cozy winter accessory with Arc'teryx quality.",
      brand: "Arc'teryx",
      category: "Accessories",
      price: 80,
      currency: "USD",
      image_url: "/images/products/arcteryx/delta-lt-zip-neck.jpeg",
      thumbnail_url: "/images/products/arcteryx/delta-lt-zip-neck.jpeg",
      status: "active",
      embedding_status: "pending",
      attributes_json: null,
      created_at: "2024-12-09T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
    "arc-10": {
      id: "arc-10",
      sku: "ARC-26845-BORDEAUX-L",
      title: "Beta LT Jacket",
      description:
        "Lightweight, packable GORE-TEX shell for trekking and hiking. Reduced weight with essential weather protection.",
      brand: "Arc'teryx",
      category: "Shell Jackets",
      price: 550,
      currency: "USD",
      image_url: "/images/products/arcteryx/beta-lt-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/beta-lt-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      attributes_json: null,
      created_at: "2024-12-10T10:00:00Z",
      updated_at: "2024-12-15T10:00:00Z",
    },
  };

  return products[id] || null;
}
